/**
 * Types and functions to perform AJAX requests.
 *
 * This replaces the ajax controllers from ExtJs.
 * @packageDocumentation
 */
import type { CancelToken, CancelTokenSource } from 'axios';
import axios from 'axios';
import { omit } from 'lodash';

import type { CodedError, HttpCancelledError, HttpError } from './Error';
import { handleFailure, handleResponse } from './AjaxImpl';
import type { VoidlessSumType } from '../lib';
import { AjaxRequestStoreApi } from '../lib';

/**
 * The HTTP Method to use for a request.
 */
export enum HttpMethod {
  POST = 'post',
  GET = 'get'
}

/**
 * Options for static configuring an http request function.
 */
export interface StaticRequestOptions {
  /** The operation to perform (backend method) */
  operation: string;
  /** The HTTP method */
  method: HttpMethod;
}

/**
 * Options needed at request time.
 *
 * NOTE: if you add properties here, be sure to ignore them in the FormData assembly inside createRequest!
 */
export interface DynamicRequestOptions {
  /** Setter for load mask visibility state. */
  setLoading?: (visible: boolean) => void;
  /**
   * Optional cancel token that can be used to cancel this request.
   *
   * You would need to create a token source using `createCancelTokenSource()` and pass in source.token.
   * Then, you can cancel this request using `source.cancel()`
   *
   * If multiple requests share a token, they will all be cancelled.
   *
   * If you don't pass a cancelToken, all requests of one operation will use a shared one so you can cancel all instances of an operation using `request.cancelAll()`.
   */
  cancelToken?: CancelToken;
  /** The base url of the portlet. */
  url: string;
  /**
   * Callback to get notified of upload progress (in %) for this request.
   *
   * If the progress is not calculatable (total is unknown), the value is set to 0.
   * You can use this to show an infinite/pulsating progress bar while progress is 0.
   */
  setUploadProgress?: (progress: number) => void;
}

/**
 * Data returned by a successful request.
 *
 * @typeParam T The type of the response.
 */
export interface SuccessResponse<T> {
  success: true;
  /** The parsed response from the back-end */
  data: T;
  /** The length of the data, if it is an array, as calculated by the back-end. */
  dataLength: T extends Array<unknown> ? number : never;
}
/**
 * Data returned by a failed request.
 */
export interface FailureResponse {
  success: false;
  error: HttpError | HttpCancelledError | CodedError;
}
/**
 * The return value of an ajax request.
 *
 * @typeParam T The type of the response.
 */
export type AjaxResponse<T> = SuccessResponse<T> | FailureResponse;

type ActualRequest<Req, Res> = (
  params: VoidlessSumType<Req, DynamicRequestOptions>
) => Promise<SuccessResponse<Res>>;

/**
 *  Async function takes all dynamic parameters as argument, performs the request and returns a promise containing an AjaxResponse.
 *  You can execute the operation by simply calling this function.
 *
 *  If you don't pass your own cancelToken, `request.cancelAllRunningRequests` will cancell all running requests.
 *
 */
export interface RequestWithCancelAll<Req, Res>
  extends ActualRequest<Req, Res> {
  /** Cancel all running requests with this operation. */
  cancelAllRunningRequests: () => void;
  /**
   * Generates request url with origin path based on url parameters that are passed.
   *
   * @example generateUrl({ url, force: true, download: 'raw' }) => {url}&force=true&download=raw
   */
  generateUrl: (params: VoidlessSumType<Req, DynamicRequestOptions>) => string;
}

/**
 * Ignored request params keys.
 */
const IGNORED_PARAMS = [
  'url',
  'setLoading',
  'cancelToken',
  'setUploadProgress'
];

/**
 * Creates form data for POST request method.
 *
 * @param params structure of data keys and values
 */
function getFormData(params: Record<string, unknown>) {
  const formData = new FormData();
  for (const key in params) {
    if (IGNORED_PARAMS.includes(key)) {
      continue;
    }
    const value: unknown = params[key];
    if (value instanceof Blob) {
      // Blob or File case. If File, set name. If not, undefined.
      // NOTE: since there is no recursion here (and FormData is flat anyway), File/Blob objects must be on the first level.
      formData.append(key, value as Blob | string, (value as File).name);
    } else if (typeof value === 'string') {
      formData.append(key, value);
    } else if (value !== undefined && value !== null) {
      // objects, booleans, numbers, ...
      formData.append(key, JSON.stringify(value));
    }
  }

  return formData;
}

/** Helper type that evaluates to never if the given type is an array type. */
export type NonArrayOrNever<T, Val> = T extends Array<unknown> ? never : Val;
// Array case
export function createRequest<Req, Res extends Array<unknown>>(
  options: StaticRequestOptions & { returnsArray: true }
): RequestWithCancelAll<Req, Res>;

// Value case
export function createRequest<
  Req,
  Res extends string | number | boolean | unknown | void
>(
  options: NonArrayOrNever<Res, StaticRequestOptions & { returnsArray?: false }>
): RequestWithCancelAll<Req, Res>;

/**
 * Create a function that will perform an ajax request.
 *
 * The static/unchanging configuration is captured and therefore not needed when actually performing a request.
 *
 * We now force you to specify 'returnsArray: true' when creating a request that returns an array.
 * This was necessary to resolve the annoying ambiguity of our back-end that it always returns an array,
 * and the only solution because types are completely erased at runtime.
 *
 *
 * @typeParam Req The type of the request parameters. Note that File/Blob objects need to be top-level properties for now.
 * @typeParam Res The type contained in the `response::data` array or `response::data[0]`, depending on whether it is an array type.
 *
 * @param opts The static request configuration.
 *
 * @return a function takes all dynamic parameters as argument, performs the request and returns a promise containing an AjaxResponse.
 */
export function createRequest<Req, Res>({
  returnsArray = false,
  ...options
}: StaticRequestOptions & { returnsArray?: boolean }): RequestWithCancelAll<
  Req,
  Res
> {
  let sharedCancelSource = createCancelTokenSource();

  /**
   * Perform an AJAX request.
   *
   *
   * @param The dynamic request parameters.
   * @return The response.
   */
  return Object.assign(
    async (
      params: VoidlessSumType<Req, DynamicRequestOptions>
    ): Promise<SuccessResponse<Res>> => {
      // count active requests
      AjaxRequestStoreApi.getState().increment();
      params.setLoading?.(true);

      const response = await axios({
        // common settings
        url: params.url,
        method: options.method,
        headers: {
          Expires: '-1',
          'Cache-Control':
            'no-store, no-cache, must-revalidate, Post-Check=0, Pre-Check=0',
          Pragma: 'no-cache',
          'X-Requested-With': 'XMLHttpRequest'
        },
        responseType: 'json',
        timeout: 60_000 * 30,
        maxContentLength: 5_000_000,
        cancelToken: params.cancelToken ?? sharedCancelSource.token,
        onUploadProgress: params.setUploadProgress
          ? (e: ProgressEvent) =>
              params.setUploadProgress?.(
                Math.round(e.lengthComputable ? (e.loaded * 100) / e.total : 0)
              )
          : undefined,

        // specific settings
        params: {
          ...(options.method === HttpMethod.GET && params),
          operation: options.operation
        },
        data:
          options.method === HttpMethod.POST
            ? getFormData((params as unknown) as Record<string, unknown>)
            : null
      })
        .then(r => handleResponse<Res>(r, returnsArray))
        .catch(e => handleFailure(e));

      // TODO: exclude params.{url, setLoading} from 'data' when using json. (setting them to 'undefined' should work
      // TODO: use json body once the back-end supports that

      AjaxRequestStoreApi.getState().decrement();
      params.setLoading?.(false);
      console.debug(`operation ${options.operation} returned`, response);
      return response.success ? response : Promise.reject(response);
    },
    {
      cancelAllRunningRequests() {
        sharedCancelSource.cancel(
          `cancelling all ${options.operation} requests at once`
        );
        // issue a new token source since we expired this one
        sharedCancelSource = createCancelTokenSource();
      },
      generateUrl(params: VoidlessSumType<Req, DynamicRequestOptions>): string {
        return [
          params.url,
          ...Object.entries({
            ...(options.method === HttpMethod.GET &&
              omit(params, ...IGNORED_PARAMS)),
            operation: options.operation
          }).flatMap(param => (param[1] === undefined ? [] : param.join('=')))
        ].join('&');
      }
    }
  );
}

/**
 * Create a cancel token source.
 *
 * The token to be passed into an ajax request can be retrieved using `source.token`.
 * Then, the request can later be cancelled using `source.cancel()`.
 *
 * If multiple requests share a token, they will all be cancelled.
 *
 */
// this export is here, so we don't need to have axios in scope in the portlets
export const createCancelTokenSource = (): CancelTokenSource =>
  axios.CancelToken.source();

// array case
export function createRequestCancellingPrevious<
  Req,
  Res extends Array<unknown>
>(
  options: StaticRequestOptions & { returnsArray: true }
): ActualRequest<Req, Res>;

// Value case
export function createRequestCancellingPrevious<
  Req,
  Res extends string | number | boolean | unknown | void
>(
  options: NonArrayOrNever<Res, StaticRequestOptions & { returnsArray?: false }>
): ActualRequest<Req, Res>;

/**
 * A wrapper around createRequest that ensures only one request is active at a time (per user ofc).
 *
 * If another request is started, the previous in-flight request will be cancelled.
 * This is really useful for e.g. search-as-you-type using a form that continuously submits with a debounce, like useManagedForm can.
 *
 * @param returnsArray passed to createRequest
 * @param options passed to createRequest
 *
 */
export function createRequestCancellingPrevious<Req, Res>({
  returnsArray = false,
  ...options
}: StaticRequestOptions & { returnsArray?: boolean }): ActualRequest<Req, Res> {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const request = createRequest<Req, Res>({ returnsArray, ...options });
  // Shared var between invocations
  let sharedCancelSource: CancelTokenSource | null = null;

  return params => {
    // cancel the previous request
    sharedCancelSource?.cancel(options.operation);
    // store the new cancel token source
    sharedCancelSource = createCancelTokenSource();
    return request({
      ...params,
      cancelToken: sharedCancelSource.token
    });
  };
}

// TODO: maybe move to some unit tests
// test that all cases work
/*
const success1 = createRequest<void, DocumentInformation>({
  operation: 'test',
  method: HttpMethod.GET
});
const success2 = createRequest<void, DocumentInformation[]>({
  operation: 'test',
  method: HttpMethod.GET,
  returnsArray: true
});

const failure1 = createRequest<void, DocumentInformation>({
  operation: 'test',
  method: HttpMethod.GET,
  returnsArray: true
});
const failure2 = createRequest<void, DocumentInformation[]>({
  operation: 'test',
  method: HttpMethod.GET,
  returnsArray: false
});
*/
